/**
 * Stock counts, batch by batch.
 *
 * The counted figure is never prefilled with what the system expects: seeding
 * it turns counting into confirming. Applying a count corrects stock against
 * what is on hand at that moment, because sales carry on while people count;
 * the discrepancy shown stays counted − expected from when the count began.
 */

import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardList, Plus } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { Button, Card, ConfirmDialog, EmptyState, ErrorState, Input, Modal, Spinner, Textarea, cn } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { usePharmacy, usePharmacyWrite, type ListOf } from './data';
import { ErrorLine, StatusBadge, Table, Td, errorText } from './parts';
import { LocationPicker, StockTabs, useActionBranch } from './MedicinesPage';

interface CountSummary {
  id: string;
  reference: string;
  branch_name: string;
  status: string;
  started_at: string;
  applied_at: string | null;
  lines: number;
}

interface CountLine {
  id: string;
  productId: string;
  productName: string;
  batchId: string | null;
  batchNumber: string | null;
  expiryDate: string | null;
  expectedQuantity: number;
  countedQuantity: number | null;
  discrepancy: number | null;
  adjustmentId: string | null;
}

interface CountView {
  id: string;
  reference: string;
  branchId: string;
  status: string;
  startedAt: string;
  appliedAt: string | null;
  note: string | null;
  lines: CountLine[];
}

export function CountsPage(): ReactNode {
  const { can } = useAuth();
  const counts = usePharmacy<ListOf<CountSummary>>('/counts');
  const [starting, setStarting] = useState(false);

  return (
    <div>
      <PageHeader
        title="Medicines & Stock"
        description="Count each batch on the shelf; an approver applies the count"
        action={can('pharmacy:stock') ? <Button icon={<Plus className="size-4" />} onClick={() => setStarting(true)}>Start a count</Button> : undefined}
      />
      <StockTabs />
      <Card padded={false}>
        {counts.isError ? (
          <div className="p-4"><ErrorState message="Could not load counts." onRetry={() => void counts.refetch()} /></div>
        ) : !counts.data ? (
          <Spinner />
        ) : counts.data.data.length === 0 ? (
          <EmptyState icon={<ClipboardList className="size-8" />} title="No counts yet" description="Start one for a location to list every batch it holds." />
        ) : (
          <Table head={['Count', 'Location', 'Started', 'Lines', 'Status']}>
            {counts.data.data.map((c) => (
              <tr key={c.id} className="hover:bg-slate-50">
                <Td><Link to={`/pharmacy/stock/counts/${c.id}`} className="font-medium text-brand-700 hover:underline">{c.reference}</Link></Td>
                <Td>{c.branch_name}</Td>
                <Td className="tabular-nums">{c.started_at.slice(0, 16).replace('T', ' ')}</Td>
                <Td className="tabular-nums">{c.lines}</Td>
                <Td><StatusBadge status={c.status} /></Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
      {starting && <StartCountModal onClose={() => setStarting(false)} />}
    </div>
  );
}

function StartCountModal({ onClose }: { onClose: () => void }): ReactNode {
  const loc = useActionBranch();
  const [note, setNote] = useState('');
  const start = usePharmacyWrite<Record<string, unknown>, { id: string }>('post', '/counts');
  const [startedId, setStartedId] = useState<string | null>(null);
  return (
    <Modal open onClose={onClose} title="Start a stock count">
      <div className="space-y-3">
        <LocationPicker value={loc.branchId} onChange={loc.setBranchId} options={loc.options} />
        <p className="text-sm text-slate-600">Every batch this location holds becomes a line, with what the system says now.</p>
        <Textarea label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
        <ErrorLine error={start.error} />
        {startedId ? (
          <div className="flex justify-end">
            <Link to={`/pharmacy/stock/counts/${startedId}`} className="text-sm font-medium text-brand-700 hover:underline">Open the count →</Link>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button
              disabled={!loc.branchId}
              loading={start.isPending}
              onClick={() => start.mutate({ branchId: loc.branchId, note: note.trim() || null }, { onSuccess: (r) => setStartedId(r.id) })}
            >
              Start
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

export function CountDetailPage(): ReactNode {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const count = usePharmacy<CountView>(`/counts/${id}`);
  const record = usePharmacyWrite<{ lineId: string; countedQuantity: number }>('post', `/counts/${id}/lines`);
  const apply = usePharmacyWrite<Record<string, never>>('post', `/counts/${id}/apply`);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const cancel = usePharmacyWrite<Record<string, never>>('post', `/counts/${id}/cancel`);
  const [cancelling, setCancelling] = useState(false);
  const navigate = useNavigate();

  if (count.isError) return <ErrorState message="Could not load this count." onRetry={() => void count.refetch()} />;
  if (!count.data) return <Spinner />;
  const c = count.data;
  const open = c.status === 'open';
  const counted = c.lines.filter((l) => l.countedQuantity !== null).length;

  function saveLine(line: CountLine) {
    const raw = drafts[line.id];
    if (raw === undefined || !/^\d+$/.test(raw.trim())) return;
    record.mutate({ lineId: line.id, countedQuantity: Number(raw) });
  }

  return (
    <div className="space-y-5">
      <Link to="/pharmacy/stock/counts" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="size-4" aria-hidden /> Stock counts
      </Link>
      <PageHeader
        title={`Count ${c.reference}`}
        description={`${counted} of ${c.lines.length} lines counted`}
        action={
          open && (can('pharmacy:stock_approve') || can('pharmacy:stock')) ? (
            <div className="flex flex-wrap gap-2">
              {can('pharmacy:stock') && (
                <Button variant="secondary" onClick={() => { cancel.reset(); setCancelling(true); }}>Cancel count</Button>
              )}
              {can('pharmacy:stock_approve') && (
                <Button disabled={counted === 0} onClick={() => setConfirming(true)}>Apply count</Button>
              )}
            </div>
          ) : (
            <StatusBadge status={c.status} />
          )
        }
      />
      {open && (
        <p className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
          Enter what you see on the shelf, in base units. Applying corrects each batch to your figure against what is on hand
          at that moment; lines left blank are not changed.
        </p>
      )}
      <ErrorLine error={record.error ?? apply.error} />
      <Card padded={false}>
        <Table head={['Item', 'Batch', 'Expiry', 'Expected', 'Counted', 'Difference']} minWidth={760}>
          {c.lines.map((l) => (
            <tr key={l.id}>
              <Td className="font-medium">{l.productName}</Td>
              <Td className="font-mono text-xs">{l.batchNumber ?? '—'}</Td>
              <Td className="tabular-nums">{l.expiryDate ?? '—'}</Td>
              <Td className="tabular-nums">{l.expectedQuantity}</Td>
              <Td>
                {open && can('pharmacy:stock') ? (
                  <Input
                    aria-label={`Counted ${l.productName} ${l.batchNumber ?? ''}`}
                    inputMode="numeric"
                    className="w-24"
                    placeholder={l.countedQuantity === null ? 'Count' : String(l.countedQuantity)}
                    value={drafts[l.id] ?? ''}
                    onChange={(e) => setDrafts((d) => ({ ...d, [l.id]: e.target.value }))}
                    onBlur={() => saveLine(l)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveLine(l);
                    }}
                  />
                ) : (
                  <span className="tabular-nums">{l.countedQuantity ?? '—'}</span>
                )}
              </Td>
              <Td className={cn('tabular-nums', (l.discrepancy ?? 0) < 0 && 'text-red-700', (l.discrepancy ?? 0) > 0 && 'text-emerald-700')}>
                {l.discrepancy === null ? '—' : l.discrepancy > 0 ? `+${l.discrepancy}` : l.discrepancy}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>
      <ConfirmDialog
        open={confirming}
        title="Apply this count?"
        description="Each counted batch is corrected to your figure against what is on hand now, as approved adjustments in your name."
        confirmLabel="Apply"
        tone="primary"
        loading={apply.isPending}
        onClose={() => setConfirming(false)}
        onConfirm={() => apply.mutate({}, { onSuccess: () => setConfirming(false) })}
      />
      <ConfirmDialog
        open={cancelling}
        title="Cancel this count?"
        description="Nothing will be applied. Figures already entered stay on the cancelled count for reference."
        confirmLabel="Cancel count"
        loading={cancel.isPending}
        error={errorText(cancel.error)}
        onClose={() => setCancelling(false)}
        onConfirm={() => cancel.mutate({}, { onSuccess: () => navigate('/pharmacy/stock/counts') })}
      />
    </div>
  );
}
